import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { fromRow, toRow } from '../lib/rowMappers';
import type { 
  FitStore, 
  User, 
  Client, 
  ClientStatus, 
  Program,
  WorkoutLog,
  LoggedSet,
  Meal,
  NutritionLog,
  Message,
  ProgressPhoto,
  Achievement,
  Resource,
  FeedEvent,
  RecoveryScore,
  BiometricData,
  Session,
  WorkoutSection,
  PlannedExercise,
  SetPrescription,
  SectionType,
  SessionStatus,
  CoachingNote,
  CheckIn,
  Lead,
  Appointment,
  GlobalExercise,
  BodyMapAssessment,
  BodyMapMark,
  BodyMapTriage,
  CoachingPlan,
  ClientBilling
} from './types';
import type { BodyMapCategory, BodyZoneId } from '../lib/bodyMap';

// A real (uuid) primary key for a DB-backed row. App-minted string ids like
// `lead_169…` are fine for nested JSONB, but Postgres uuid columns reject them.
const newId = (): string => crypto.randomUUID();
const isUuid = (s: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

// Body-map assessments arrive with their child marks nested (body_map_marks(*)).
// fromRow only camelizes one level, so map the nested mark rows explicitly.
const mapBodyMapAssessments = (rows: Record<string, any>[] | null | undefined): BodyMapAssessment[] =>
  (rows || []).map((row) => {
    const marks: BodyMapMark[] = ((row.body_map_marks as Record<string, any>[]) || []).map((m) => ({
      id: m.id,
      zoneId: m.zone_id as BodyZoneId,
      category: m.category as BodyMapCategory,
      severity: m.severity ?? undefined,
      note: m.note ?? undefined,
    }));
    return {
      id: row.id,
      clientId: row.client_id,
      coachId: row.coach_id,
      assessedOn: row.assessed_on,
      note: row.note ?? undefined,
      marks,
      createdAt: row.created_at,
    };
  });

// ---- demo dataset -----------------------------------------------------------
// One factory feeds BOTH demo mode and the first-run tour sandbox: the tour
// overlays this data (bound to the real coach's id) in memory so every screen
// looks alive while isLive() suppresses all writes.
const buildDemoData = (coachId: string) => {
  // Rich demo clients for roster triage + CRM hub
  const demoClients: Client[] = [
    { id: 'c1', name: 'Jordan Hale', email: 'jordan@client.com', role: 'client', coachId: coachId, status: 'green', engagementScore: 95, subscriptionStatus: 'active',
      planId: 'plan_demo_2', priceCents: 50000, billingInterval: 'month', currency: 'usd',
      phone: '(415) 555-0142', lifecycle: 'active', startDate: '2024-11-04', goals: 'Hit a 275 bench, stay under 12% BF', injuries: 'None current — old right wrist sprain, fine now',
      coachingNotes: [
        { id: 'n_c1_1', text: 'Loves upper-body days, drags feet on conditioning. Keep cardio short and competitive.', createdAt: new Date(Date.now() - 1000*60*60*24*9).toISOString() },
        { id: 'n_c1_2', text: 'Mentioned a work trip the week of the 23rd — plan a deload around it.', createdAt: new Date(Date.now() - 1000*60*60*24*2).toISOString() },
      ] },
    { id: 'c2', name: 'Sam Patel', email: 'sam@client.com', role: 'client', coachId: coachId, status: 'yellow', engagementScore: 72, subscriptionStatus: 'active',
      phone: '(415) 555-0198', lifecycle: 'active', startDate: '2025-02-18', goals: 'General strength + mobility for desk-job back pain', injuries: 'Lower-back tightness — keep spinal loading moderate',
      coachingNotes: [
        { id: 'n_c2_1', text: 'Band work is helping the back. Progress landmine before barbell deads.', createdAt: new Date(Date.now() - 1000*60*60*24*5).toISOString() },
      ] },
    { id: 'c3', name: 'Taylor Kim', email: 'taylor@client.com', role: 'client', coachId: coachId, status: 'red', engagementScore: 45, subscriptionStatus: 'active', lastLoggedAt: new Date(Date.now() - 1000*60*60*30).toISOString(),
      phone: '(415) 555-0211', lifecycle: 'paused', startDate: '2025-04-30', goals: 'Was training for a 10k — paused after travel', injuries: 'None reported',
      coachingNotes: [
        { id: 'n_c3_1', text: 'Went quiet 2 weeks ago. Sent a check-in text — no reply yet. Follow up Monday.', createdAt: new Date(Date.now() - 1000*60*60*24*4).toISOString() },
      ] },
    { id: 'c4', name: 'Morgan Lee', email: 'morgan@client.com', role: 'client', coachId: coachId, status: 'green', engagementScore: 88, subscriptionStatus: 'active',
      phone: '(415) 555-0176', lifecycle: 'active', startDate: '2024-08-12', goals: 'Sled/strongman conditioning, keep mobility high', injuries: 'Right ankle — limit deep loaded dorsiflexion',
      coachingNotes: [] },
  ];

  // Demo program with supersets to showcase the engine
  const demoProgram: Program = {
    id: 'prog-demo-01',
    title: 'Demo Strength Block • Week 1',
    coachId: coachId,
    workouts: [
      {
        id: 'w1',
        title: 'Upper Power + Core',
        exercises: [
          { id: 'e1', name: 'Bench Press (Barbell)', sets: 4, reps: '6', restSeconds: 120, supersetId: 'ssA' },
          { id: 'e2', name: 'Bent Over Row', sets: 4, reps: '6', restSeconds: 120, supersetId: 'ssA' },
          { id: 'e3', name: 'Overhead Press', sets: 3, reps: '8', restSeconds: 90 },
          { id: 'e4', name: 'Pull Ups', sets: 3, reps: 'AMRAP', restSeconds: 90 },
        ]
      },
      {
        id: 'w2',
        title: 'Lower + Conditioning',
        exercises: [
          { id: 'e5', name: 'Back Squat', sets: 5, reps: '5', restSeconds: 180 },
          { id: 'e6', name: 'Romanian Deadlift', sets: 4, reps: '8', restSeconds: 120 },
          { id: 'e7', name: 'Walking Lunges', sets: 3, reps: '10/leg', restSeconds: 60 },
        ]
      }
    ]
  };

  const demoPrograms: Program[] = [demoProgram];

  // --- Demo weekly sessions for the board (this week, varied statuses) ---
  const weekday = (offset: number): string => {
    const d = new Date();
    const dow = (d.getDay() + 6) % 7; // Monday = 0
    d.setDate(d.getDate() - dow + offset);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${da}`; // local date — matches the board's formatter
  };
  let _pid = 0;
  const E = (name: string, sets: SetPrescription[], note?: string): PlannedExercise =>
    ({ id: `pe${++_pid}`, name, note, sets });
  const S = (type: SectionType, title: string, exercises: PlannedExercise[]): WorkoutSection =>
    ({ id: `sec${++_pid}`, type, title, exercises });
  const SESS = (clientId: string, dayOffset: number, title: string, status: SessionStatus, sections: WorkoutSection[]): Session =>
    ({ id: `sess_${clientId}_${dayOffset}`, clientId, coachId: coachId, date: weekday(dayOffset), title, status, sections });

  const demoSessions: Session[] = [
    // Jordan Hale (c1) — LAST week (lighter loads → powers the "last wk" reference + copy-forward)
    SESS('c1', -7, 'Upper Power', 'completed', [
      S('main', 'Main', [
        E('BB Incline Bench', [{ load: '35', reps: '10' }, { load: '40', reps: '10' }, { load: '40', reps: '10' }]),
        E('BB Rows', [{ load: '45', reps: '12' }, { load: '55', reps: '10' }, { load: '55', reps: '12' }]),
        E('DB Lat Raises', [{ load: '8', reps: '15' }, { load: '10', reps: '12' }, { load: '10', reps: '12' }]),
      ]),
      S('cooldown', 'Cooldown / Core', [E('Dip Holds', [{ seconds: 40 }, { seconds: 40 }, { seconds: 38 }])]),
    ]),
    SESS('c1', -5, 'Lower Strength', 'completed', [
      S('main', 'Main', [
        E('BB Box Squats', [{ load: '45', reps: '10' }, { load: '45', reps: '10' }, { load: '55', reps: '10' }], '18in box'),
        E('BB RDLs', [{ load: '10', reps: '10' }, { load: '10', reps: '10' }, { load: '10', reps: '10' }]),
      ]),
    ]),
    // Jordan Hale (c1) — on track: 3 sessions
    SESS('c1', 0, 'Upper Power', 'completed', [
      S('main', 'Main', [
        E('BB Incline Bench', [{ load: '45', reps: '10' }, { load: '45', reps: '10' }, { load: '45', reps: '10' }]),
        E('BB Rows', [{ load: '45', reps: '15' }, { load: '65', reps: '10' }, { load: '65', reps: '15' }]),
        E('DB Lat Raises', [{ load: '10', reps: '15' }, { load: '10', reps: '15' }, { load: '10', reps: '15' }]),
      ]),
      S('cooldown', 'Cooldown / Core', [E('Dip Holds', [{ seconds: 46 }, { seconds: 46 }, { seconds: 45 }])]),
    ]),
    SESS('c1', 2, 'Lower Strength', 'completed', [
      S('main', 'Main', [
        E('BB Box Squats', [{ load: '45', reps: '10' }, { load: '55', reps: '10' }, { load: '65', reps: '10' }], '18in box'),
        E('BB RDLs', [{ load: '10', reps: '10' }, { load: '10', reps: '10' }, { load: '10', reps: '10' }]),
      ]),
      S('mobility', 'Mobility / Weakness', [E('Band Walks', [{}], 'Blue band')]),
    ]),
    SESS('c1', 4, 'Full Body', 'planned', [
      S('main', 'Main', [E('KB Box Squats', [{ load: '35', reps: '12' }, { load: '44', reps: '10' }, { load: '44', reps: '10' }], '12in box')]),
    ]),
    // Sam Patel (c2) — partly programmed
    SESS('c2', 0, 'Push', 'completed', [
      S('main', 'Main', [
        E('Banded Shoulder Press', [{ load: 'Medium band', reps: '15' }, { load: 'Medium band', reps: '15' }, { load: 'Medium band', reps: '15' }]),
        E('DB Overhead Extensions', [{ load: '15', reps: '10' }, { load: '15', reps: '10' }, { load: '15', reps: '12' }]),
      ]),
    ]),
    SESS('c2', 3, 'Leg Day', 'planned', [
      S('main', 'Main', [
        E('Landmine Squats', [{ load: '25', reps: '12' }, { load: '25', reps: '12' }, { load: '25', reps: '12' }], 'Blue band'),
        E('Banded Ham Curls', [{ reps: '100' }], 'Purple band'),
      ]),
    ]),
    // Taylor Kim (c3) — intentionally none → "not programmed" flag
    // Morgan Lee (c4) — partial
    SESS('c4', 0, 'Upper', 'completed', [
      S('main', 'Main', [E('Medball Chest Press', [{ load: '20', seconds: 30 }, { load: '20', seconds: 30 }, { load: '20', seconds: 30 }])]),
    ]),
    SESS('c4', 1, 'Mobility', 'completed', [
      S('mobility', 'Mobility / Weakness', [E('Ankle Mobilizations & Hip Openers', [{}]), E('Plate Mobilizations', [{}])]),
    ]),
    SESS('c4', 2, 'Lower', 'partial', [
      S('main', 'Main', [
        E('Sled Pulls', [{ load: '160', laps: 3 }]),
        E('KB Single Leg Deads', [{ load: '44', reps: '10' }, { load: '44', reps: '10' }]),
      ]),
    ]),
  ];

  // A couple of logged sessions for Jordan so the History tab has real content
  const demoWorkoutLogs: WorkoutLog[] = [
    {
      id: 'log_c1_1', clientId: 'c1', workoutId: 'sess_c1_0', workoutTitle: 'Upper Power', date: weekday(0),
      exercises: {
        'BB Incline Bench': [
          { weight: '45', reps: '10', completedAt: new Date().toISOString() },
          { weight: '45', reps: '10', completedAt: new Date().toISOString() },
          { weight: '55', reps: '8', completedAt: new Date().toISOString(), coachFeedback: 'Bar speed looked great on these — bump to 60 next week.' },
        ],
        'BB Rows': [
          { weight: '65', reps: '10', completedAt: new Date().toISOString() },
          { weight: '65', reps: '12', completedAt: new Date().toISOString() },
        ],
      },
    },
    {
      id: 'log_c1_2', clientId: 'c1', workoutId: 'sess_c1_2', workoutTitle: 'Lower Strength', date: weekday(2),
      exercises: {
        'BB Box Squats': [
          { weight: '45', reps: '10', completedAt: new Date().toISOString() },
          { weight: '55', reps: '10', completedAt: new Date().toISOString() },
          { weight: '65', reps: '10', completedAt: new Date().toISOString() },
        ],
      },
    },
  ];

  // Weekly check-ins — Jordan has a 2-week trend but owes THIS week (due);
  // Sam is current (done); Taylor & Morgan owe this week too.
  const CI = (clientId: string, weekMonOffset: number, fields: Partial<CheckIn>): CheckIn => ({
    id: `ci_${clientId}_${weekMonOffset}`,
    clientId,
    coachId: coachId,
    weekOf: weekday(weekMonOffset),
    createdAt: new Date().toISOString(),
    ...fields,
  });
  const demoCheckIns: CheckIn[] = [
    CI('c1', -14, { weightLbs: 188, bodyFatPct: 14.5, skeletalMuscleMass: 88, sleep: '7–8h, solid', diet: 'On point', recovery: 'Good' }),
    CI('c1', -7, { weightLbs: 186, bodyFatPct: 13.8, skeletalMuscleMass: 88.5, sleep: 'Good', diet: 'One cheat meal', recovery: 'Slight L shoulder tightness' }),
    CI('c2', 0, { weightLbs: 205, bodyFatPct: 22, skeletalMuscleMass: 82, sleep: 'Poor — work stress', diet: 'Struggled midweek', recovery: 'Back felt better' }),
    CI('c4', -7, { weightLbs: 165, bodyFatPct: 16, skeletalMuscleMass: 74, sleep: 'Great', diet: 'Clean', recovery: 'Ankle stable' }),
  ];

  // Demo leads — varied stages + follow-up dates (some overdue → "due")
  const dayISO = (offset: number): string => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const demoLeads: Lead[] = [
    { id: 'lead_1', coachId: coachId, name: 'Dana Cruz', phone: '(415) 555-0188', email: 'dana@email.com', source: 'IG DM', status: 'consult_done', consultAt: dayISO(-4), followUpAt: dayISO(-2), notes: 'Great consult — wants to start a 12-week cut. Send the proposal.', createdAt: dayISO(-9) },
    { id: 'lead_2', coachId: coachId, name: 'Marcus Bell', email: 'marcus@email.com', source: 'Referral', status: 'contacted', followUpAt: dayISO(0), notes: 'Referred by Jordan. Replied, wants a call this week.', createdAt: dayISO(-6) },
    { id: 'lead_3', coachId: coachId, name: 'Priya Shah', phone: '(415) 555-0240', source: 'Walk-in', status: 'consult_booked', consultAt: dayISO(5), followUpAt: dayISO(3), notes: 'Booked a consult for next week. Confirm the day before.', createdAt: dayISO(-3) },
    { id: 'lead_4', coachId: coachId, name: 'Tom Reyes', email: 'tom@email.com', source: 'Website', status: 'new', notes: 'Filled out the contact form. Not yet reached out.', createdAt: dayISO(-1) },
    { id: 'lead_5', coachId: coachId, name: 'Jen Kessler', email: 'jen@email.com', source: 'Referral', status: 'won', notes: 'Signed up! Converted.', createdAt: dayISO(-20) },
    { id: 'lead_6', coachId: coachId, name: 'Chris Vale', source: 'IG DM', status: 'lost', notes: 'Went with a cheaper option. Check back in 3 months.', createdAt: dayISO(-25) },
  ];

  // Appointments — datetimes built in the viewer's local zone so the demo
  // agenda reads correctly wherever it's opened.
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const apptAt = (dayOffset: number, hour: number, minute = 0): string => {
    const d = new Date();
    d.setDate(d.getDate() + dayOffset);
    d.setHours(hour, minute, 0, 0);
    return d.toISOString();
  };
  const demoAppointments: Appointment[] = [
    { id: 'appt_1', coachId: coachId, clientId: 'c1', attendeeName: 'Jordan Hale', attendeeEmail: 'jordan@client.com',
      title: 'Upper-body session', location: 'Downtown Studio', startsAt: apptAt(1, 9), endsAt: apptAt(1, 9, 45), timezone: tz, status: 'scheduled', exdate: [], createdAt: dayISO(-2) },
    { id: 'appt_2', coachId: coachId, clientId: 'c2', attendeeName: 'Sam Patel', attendeeEmail: 'sam@client.com',
      title: 'Form check + back rehab call', location: 'Zoom', notes: 'Review landmine RDL video before the call.', startsAt: apptAt(2, 14), endsAt: apptAt(2, 14, 30), timezone: tz, status: 'scheduled', exdate: [], createdAt: dayISO(-1) },
    { id: 'appt_3', coachId: coachId, leadId: 'lead_3', attendeeName: 'Priya Shah', title: 'Intro consult',
      location: 'Downtown Studio', notes: 'Walk-in lead — bring the 12-week proposal.', startsAt: apptAt(5, 11), endsAt: apptAt(5, 11, 45), timezone: tz, status: 'scheduled', exdate: [], createdAt: dayISO(-3) },
    { id: 'appt_4', coachId: coachId, attendeeName: 'Equipment vendor', title: 'Rack delivery walkthrough',
      location: 'Downtown Studio', startsAt: apptAt(0, 16), endsAt: apptAt(0, 16, 30), timezone: tz, status: 'scheduled', exdate: [], createdAt: dayISO(-1) },
    { id: 'appt_5', coachId: coachId, clientId: 'c4', attendeeName: 'Morgan Lee', attendeeEmail: 'morgan@client.com',
      title: 'Weekly check-in call', location: 'Zoom', startsAt: apptAt(3, 8), endsAt: apptAt(3, 8, 30), timezone: tz, status: 'scheduled',
      rrule: 'FREQ=WEEKLY', exdate: [], createdAt: dayISO(-14) },
  ];


  // Pricing plans + webhook-style billing rows so the money features read alive
  // in demo mode and the tour (writes are suppressed by isLive()).
  const demoPlans: CoachingPlan[] = [
    { id: 'plan_demo_1', coachId, name: 'Foundation', priceCents: 30000, billingInterval: 'month', currency: 'usd',
      description: 'Twice-weekly coaching for building the habit.', inclusions: { sessionsPerWeek: 2, sessionLengthMin: 60 }, isActive: true, createdAt: dayISO(-40) },
    { id: 'plan_demo_2', coachId, name: 'Performance', priceCents: 50000, billingInterval: 'month', currency: 'usd',
      description: 'Full programming, nutrition, and weekly check-ins.', inclusions: { sessionsPerWeek: 3, sessionLengthMin: 60 }, isActive: true, createdAt: dayISO(-40) },
  ];
  const demoBilling: ClientBilling[] = [
    { id: 'bill_c1', clientId: 'c1', coachId, stripeAccountId: 'acct_demo', status: 'active',
      amountCents: 50000, currency: 'usd', billingInterval: 'month', currentPeriodEnd: new Date(Date.now() + 1000*60*60*24*12).toISOString() },
    { id: 'bill_c3', clientId: 'c3', coachId, stripeAccountId: 'acct_demo', status: 'past_due',
      amountCents: 30000, currency: 'usd', billingInterval: 'month' },
  ];

  return {
    clients: demoClients,
    masterPrograms: demoPrograms,
    workoutLogs: demoWorkoutLogs,
    sessions: demoSessions,
    checkIns: demoCheckIns,
    leads: demoLeads,
    appointments: demoAppointments,
    coachingPlans: demoPlans,
    clientBilling: demoBilling,
    locations: [{ id: 'loc-main', orgId: 'demo-org', name: 'Downtown Studio', address: '123 Main St' }],
    currentLocationId: 'loc-main',
    feedEvents: [
      { id: 'f1', userId: 'c1', userName: 'Jordan Hale', type: 'COMPLETION' as const, title: 'New PR!', description: 'Jordan just hit a 225lb bench for 5!', timestamp: new Date(), reactions: {} },
    ],
    messages: [
      { id: 'm1', senderId: coachId, receiverId: 'c1', text: 'Hey Jordan, check out this program!', createdAt: { seconds: Date.now()/1000 - 100 }, read: true },
      { id: 'm2', senderId: coachId, receiverId: 'c1', text: 'Summer Shred Sneak Peek - 1 week free program. Duration: 1 week', createdAt: { seconds: Date.now()/1000 - 80 }, read: true },
      { id: 'm3', senderId: 'c1', receiverId: coachId, text: 'Amazing, will give it a go!', createdAt: { seconds: Date.now()/1000 - 60 }, read: true },
    ],
    globalExercises: [
      { id: 'ge1', name: 'Bench Press (Barbell)', muscleGroup: 'Chest', equipment: 'Barbell', mechanics: 'Compound' as const, defaultSets: 4, defaultReps: '6', defaultRest: 120 },
      { id: 'ge2', name: 'Bent Over Row', muscleGroup: 'Back', equipment: 'Barbell', mechanics: 'Compound' as const, defaultSets: 4, defaultReps: '6', defaultRest: 120 },
    ],
  };
};

export const useFitStore = create<FitStore>()((set, get) => {
  // Real mode persists to Supabase; demo mode AND the first-run tour sandbox
  // keep everything in local state (the tour overlays mock data on a real
  // signed-in coach — no write may reach the real DB while it runs).
  const isLive = () => !get().tourMode && localStorage.getItem('fitflex_demo') !== 'true';
  // Surface write failures instead of swallowing them — an optimistic UI update
  // plus a silently-rejected write would "look saved" yet vanish on refresh.
  const reportWrite = (label: string, error: { message: string } | null | undefined) => {
    if (!error) return;
    console.error(`[fitflex] ${label} save failed:`, error.message);
    get().showToast(`Couldn't save ${label}`, 'error');
  };

  // ---- data loaders (called by initDataSync once authenticated) ----
  // Coach slice: roster + everything keyed to this coach. RLS also scopes each
  // table; we filter by coach_id so the coach's own row stays out of the roster.
  const loadCoachData = async (coachId: string) => {
    // Never clobber the tour sandbox — auth refreshes re-fire applySession →
    // initDataSync mid-tour; endTour() reloads for real once tourMode clears.
    if (get().tourMode) return;
    const [clientsRes, notesRes, sessionsRes, checkInsRes, leadsRes, programsRes, logsRes, appointmentsRes, exercisesRes, messagesRes, resourcesRes, bodyMapRes, triageRes, plansRes, billingRes] = await Promise.all([
      supabase.from('profiles').select('*').eq('coach_id', coachId),
      supabase.from('coaching_notes').select('*').eq('coach_id', coachId).order('created_at', { ascending: false }),
      supabase.from('sessions').select('*').eq('coach_id', coachId),
      supabase.from('check_ins').select('*').eq('coach_id', coachId),
      supabase.from('leads').select('*').eq('coach_id', coachId),
      supabase.from('master_programs').select('*').eq('coach_id', coachId),
      // workout_logs has no coach_id column — RLS (is_my_client) already scopes it to this coach's clients.
      supabase.from('workout_logs').select('*'),
      supabase.from('appointments').select('*').eq('coach_id', coachId),
      // global_exercises is a shared library (no coach_id) — the picker reaches for these.
      supabase.from('global_exercises').select('*').order('name'),
      supabase.from('messages').select('*').or(`sender_id.eq.${coachId},receiver_id.eq.${coachId}`).order('created_at', { ascending: true }),
      supabase.from('resources').select('*').eq('coach_id', coachId).order('created_at', { ascending: false }),
      // Body maps the roster's clients authored (with nested marks) + this coach's triage.
      supabase.from('body_map_assessments').select('*, body_map_marks(*)').eq('coach_id', coachId).order('assessed_on', { ascending: false }),
      supabase.from('body_map_triage').select('*').eq('coach_id', coachId),
      supabase.from('coaching_plans').select('*').eq('coach_id', coachId).order('created_at', { ascending: false }),
      // client_billing is RLS-scoped to this coach's clients; webhook-maintained cache.
      supabase.from('client_billing').select('*'),
    ]);
    const firstErr = [clientsRes, notesRes, sessionsRes, checkInsRes, leadsRes, programsRes, logsRes, appointmentsRes, exercisesRes].find((r) => r.error);
    if (firstErr?.error) {
      console.error('[fitflex] coach data load failed:', firstErr.error.message);
      get().showToast("Couldn't load your data", 'error');
    }
    // coaching_notes is its own table now — group newest-first onto each client.
    const notesByClient = new Map<string, CoachingNote[]>();
    (notesRes.data || []).forEach((r) => {
      const n = fromRow<{ id: string; clientId: string; text: string; createdAt: string }>(r);
      const arr = notesByClient.get(n.clientId) || [];
      arr.push({ id: n.id, text: n.text, createdAt: n.createdAt });
      notesByClient.set(n.clientId, arr);
    });
    const clients = (clientsRes.data || []).map((r) => {
      const c = fromRow<Client>(r);
      return { ...c, coachingNotes: notesByClient.get(c.id) || [] };
    });
    if (get().tourMode) return; // fetch started pre-tour, resolved mid-tour
    set({
      clients,
      sessions: (sessionsRes.data || []).map((r) => fromRow<Session>(r)),
      checkIns: (checkInsRes.data || []).map((r) => fromRow<CheckIn>(r)),
      leads: (leadsRes.data || []).map((r) => fromRow<Lead>(r)),
      masterPrograms: (programsRes.data || []).map((r) => fromRow<Program>(r)),
      workoutLogs: (logsRes.data || []).map((r) => fromRow<WorkoutLog>(r)),
      appointments: (appointmentsRes.data || []).map((r) => fromRow<Appointment>(r)),
      globalExercises: (exercisesRes.data || []).map((r) => fromRow<GlobalExercise>(r)),
      messages: (messagesRes.data || []).map((r) => fromRow<Message>(r)),
      resources: (resourcesRes.data || []).map((r) => fromRow<Resource>(r)),
      bodyMapAssessments: mapBodyMapAssessments(bodyMapRes.data),
      bodyMapTriage: (triageRes.data || []).map((r) => fromRow<BodyMapTriage>(r)),
      coachingPlans: (plansRes.data || []).map((r) => fromRow<CoachingPlan>(r)),
      clientBilling: (billingRes.data || []).map((r) => fromRow<ClientBilling>(r)),
      dataLoaded: true,
    });
  };

  // Client slice: the signed-in client's own profile (for habits/biometrics),
  // the sessions their coach programmed, and their workout logs.
  const loadClientData = async (profileId: string) => {
    if (get().tourMode) return;
    const [selfRes, sessionsRes, logsRes, nutritionRes, messagesRes, feedRes, photosRes, achievementsRes, billingRes] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', profileId).maybeSingle(),
      supabase.from('sessions').select('*').eq('client_id', profileId),
      supabase.from('workout_logs').select('*').eq('client_id', profileId),
      supabase.from('nutrition_logs').select('*').eq('client_id', profileId),
      supabase.from('messages').select('*').or(`sender_id.eq.${profileId},receiver_id.eq.${profileId}`).order('created_at', { ascending: true }),
      supabase.from('feed_events').select('*').order('created_at', { ascending: false }).limit(50),
      supabase.from('progress_photos').select('*').eq('client_id', profileId).order('created_at', { ascending: false }),
      supabase.from('achievements').select('*').eq('client_id', profileId),
      // Own billing row (RLS: client_id = me) — powers the paywall's "pay your coach" card.
      supabase.from('client_billing').select('*').eq('client_id', profileId),
    ]);
    const firstErr = [selfRes, sessionsRes, logsRes, nutritionRes, messagesRes, feedRes, photosRes, achievementsRes].find((r) => r.error);
    if (firstErr?.error) {
      console.error('[fitflex] client data load failed:', firstErr.error.message);
      get().showToast("Couldn't load your training", 'error');
    }
    if (get().tourMode) return; // fetch started pre-tour, resolved mid-tour

    // Photo rows store the bucket PATH (legacy Firebase rows held full URLs) —
    // sign the paths for display.
    const photos = (photosRes.data || []).map((r) => fromRow<ProgressPhoto>(r));
    const toSign = photos.filter((p) => p.url && !/^https?:/.test(p.url));
    if (toSign.length > 0) {
      const { data: signed } = await supabase.storage.from('client-photos').createSignedUrls(toSign.map((p) => p.url), 3600);
      toSign.forEach((p, i) => { p.url = signed?.[i]?.signedUrl || p.url; });
    }

    // Resources are published by the client's coach.
    const me = selfRes.data ? fromRow<Client>(selfRes.data) : null;
    let resources: Resource[] = [];
    if (me?.coachId) {
      const { data } = await supabase.from('resources').select('*').eq('coach_id', me.coachId).order('created_at', { ascending: false });
      resources = (data || []).map((r) => fromRow<Resource>(r));
    }

    if (get().tourMode) return;
    set({
      // self profile lands in `clients` so the dashboard's clients.find(id===me) works
      clients: me ? [me] : [],
      sessions: (sessionsRes.data || []).map((r) => fromRow<Session>(r)),
      workoutLogs: (logsRes.data || []).map((r) => fromRow<WorkoutLog>(r)),
      nutritionLogs: (nutritionRes.data || []).map((r) => fromRow<NutritionLog>(r)),
      messages: (messagesRes.data || []).map((r) => fromRow<Message>(r)),
      feedEvents: (feedRes.data || []).map((r) => ({ ...fromRow<FeedEvent>(r), timestamp: r.created_at })),
      progressPhotos: photos,
      achievements: (achievementsRes.data || []).map((r) => fromRow<Achievement>(r)),
      resources,
      clientBilling: (billingRes.data || []).map((r) => fromRow<ClientBilling>(r)),
      dataLoaded: true,
    });
  };

  return {
  user: null,
  clients: [],
  masterPrograms: [],
  workoutLogs: [],
  sessions: [],
  checkIns: [],
  leads: [],
  appointments: [],
  coachingPlans: [],
  clientBilling: [],
  nutritionLogs: [],
  progressPhotos: [],
  achievements: [],
  resources: [],
  globalExercises: [],
  feedEvents: [],
  messages: [],
  bodyMapAssessments: [],
  bodyMapTriage: [],
  isLoading: true,
  dataLoaded: false,
  tourMode: false,
  toast: null,
  
  // Multi-location state
  locations: [],
  currentLocationId: null,

  setUser: (user: User | null) => set({ user }),
  setLoading: (isLoading: boolean) => set({ isLoading }),

  showToast: (message: string, type: 'success' | 'error' = 'success') => set({ toast: { message, type } }),
  hideToast: () => set({ toast: null }),

  setCurrentLocation: (locationId: string) => {
    set({ currentLocationId: locationId });
    const user = get().user;
    if (user) get().initDataSync(user);
  },

  updateUserPrivacy: async (isIncognito: boolean) => {
    const user = get().user;
    if (!user) return;
    set({ user: { ...user, isIncognito } });
    if (!isLive()) return;
    const { error } = await supabase.from('profiles').update({ is_incognito: isIncognito }).eq('id', user.id);
    reportWrite('privacy', error);
  },

  signOut: async () => {
    localStorage.removeItem('fitflex_demo');
    try { await supabase.auth.signOut(); } catch { /* demo mode has no live auth session */ }
    set({ user: null, dataLoaded: false, clients: [], masterPrograms: [], messages: [], sessions: [], checkIns: [], leads: [], appointments: [], coachingPlans: [], clientBilling: [], workoutLogs: [], nutritionLogs: [], progressPhotos: [], achievements: [], resources: [], globalExercises: [], feedEvents: [], bodyMapAssessments: [], bodyMapTriage: [], locations: [], currentLocationId: null });
  },

  // Hydrate from a Supabase session: load the caller's profile row → app User,
  // then load their data slice. The lookup is by `user_id` (the auth link), NOT
  // `id`: post-decoupling a profile's own id no longer equals its auth uid.
  applySession: async (session) => {
    if (!session?.user) {
      set({ user: null, isLoading: false });
      return;
    }
    const { data } = await supabase.from('profiles').select('*').eq('user_id', session.user.id).maybeSingle();
    let row = data;
    if (!row) {
      // Self-heal: a Google login that attaches to a pre-existing auth user never
      // fires the signup trigger, so the session has no profile row. Create it
      // server-side (same defaults as the trigger) instead of stranding the user
      // on the homepage with a valid session.
      const { data: healed, error: healError } = await supabase.rpc('ensure_my_profile');
      if (healError || !healed) {
        set({ user: null, isLoading: false });
        return;
      }
      row = healed;
    }
    const profile = fromRow<User>(row);
    set({ user: profile, isLoading: false });
    get().initDataSync(profile);
  },

  syncWearableData: async () => {
    const user = get().user;
    if (!user) return;

    set({ isLoading: true });
    await new Promise(resolve => setTimeout(resolve, 2000));

    const today = new Date().toISOString().split('T')[0];
    const mockBiometrics: BiometricData = {
      steps: Math.floor(Math.random() * 5000) + 8000,
      restingHR: Math.floor(Math.random() * 10) + 55,
      sleepHours: Math.floor(Math.random() * 3) + 6,
      sleepQuality: Math.floor(Math.random() * 20) + 75,
      lastSync: new Date().toISOString()
    };

    const me = get().clients.find((c) => c.id === user.id);
    const biometrics = { ...(me?.biometrics || {}), [today]: mockBiometrics };
    set((state) => ({
      clients: state.clients.map((c) => (c.id === user.id ? { ...c, biometrics } : c)),
      isLoading: false,
    }));
    if (isLive()) {
      const { error } = await supabase.from('profiles').update({ biometrics }).eq('id', user.id);
      reportWrite('biometrics', error);
    }
  },

  calculateRecovery: (date: string): RecoveryScore => {
    const user = get().user;
    const clientData = user?.role === 'client' ? get().clients.find(c => c.id === user.id) : null;
    const biometrics = clientData?.biometrics?.[date];

    if (!biometrics) {
      return { score: 0, status: 'moderate', recommendation: 'Sync wearable data to see your recovery score.' };
    }

    const sleepScore = (biometrics.sleepHours / 8) * 50 + (biometrics.sleepQuality / 100) * 10;
    const hrScore = Math.max(0, 40 - (biometrics.restingHR - 55) * 2);
    const totalScore = Math.min(Math.round(sleepScore + hrScore), 100);

    let status: RecoveryScore['status'] = 'optimal';
    let recommendation = 'You are primed for a PR! High intensity recommended.';

    if (totalScore < 70) {
      status = 'moderate';
      recommendation = 'Solid recovery. Stick to your planned intensity.';
    }
    if (totalScore < 40) {
      status = 'low';
      recommendation = 'Recovery is low. Consider a deload or active recovery session.';
    }

    return { score: totalScore, status, recommendation };
  },

  // Beta access activation — no payment is taken. Mirrors mobile's non-iOS
  // fallthrough: client app access is free during beta; coach↔client money
  // moves through Stripe Connect (see lib/billing.ts), not this flag.
  processPayment: async (_planId: string) => {
    const user = get().user;
    if (!user) return;
    set({ user: { ...user, subscriptionStatus: 'active' } });
    if (isLive()) {
      const { error } = await supabase.from('profiles').update({ subscription_status: 'active' }).eq('id', user.id);
      reportWrite('activation', error);
    }
  },

  updateSubscriptionStatus: async (clientId: string, status: User['subscriptionStatus']) => {
    set((state) => ({ clients: state.clients.map((c) => (c.id === clientId ? { ...c, subscriptionStatus: status } : c)) }));
    if (!isLive()) return;
    const { error } = await supabase.from('profiles').update({ subscription_status: status ?? 'none' }).eq('id', clientId);
    reportWrite('subscription', error);
  },

  postFeedEvent: async (eventData: Omit<FeedEvent, 'id' | 'timestamp' | 'reactions'>) => {
    const user = get().user;
    if (!user || user.isIncognito) return;
    const event: FeedEvent = { ...eventData, id: newId(), timestamp: new Date().toISOString(), reactions: {} };
    set((state) => ({ feedEvents: [event, ...state.feedEvents] }));
    if (!isLive()) return;
    const { error } = await supabase.from('feed_events').insert({
      id: event.id,
      user_id: event.userId,
      user_name: event.userName,
      type: event.type,
      title: event.title,
      description: event.description,
      reactions: {},
      created_at: event.timestamp,
    });
    reportWrite('feed', error);
  },

  toggleReaction: async (eventId: string) => {
    const user = get().user;
    if (!user) return;
    const ev = get().feedEvents.find((e) => e.id === eventId);
    if (!ev) return;
    const reactions = { ...ev.reactions };
    if (reactions[user.id]) delete reactions[user.id];
    else reactions[user.id] = true;
    set((state) => ({ feedEvents: state.feedEvents.map((e) => (e.id === eventId ? { ...e, reactions } : e)) }));
    if (!isLive()) return;
    const { error } = await supabase.from('feed_events').update({ reactions }).eq('id', eventId);
    reportWrite('reaction', error);
  },

  addClient: async (client: Client) => {
    const coach = get().user;
    if (!coach) return;
    // A managed client: stable own uuid, no auth login yet (user_id stays null
    // until they're invited). coach_id = the coach's PROFILE id (= coach.id).
    const id = newId();
    const local: Client = { ...client, id, role: 'client', coachId: coach.id, subscriptionStatus: 'none', coachingNotes: client.coachingNotes || [] };
    set((state) => ({ clients: [...state.clients, local] }));
    if (!isLive()) return;
    // Map only real profile columns — skip studio/org/location (legacy callers
    // pass non-uuid placeholders) and coachingNotes (now its own table).
    const { error } = await supabase.from('profiles').insert({
      id,
      name: client.name,
      email: client.email || null,
      role: 'client',
      coach_id: coach.id,
      status: client.status || 'green',
      engagement_score: client.engagementScore ?? 100,
      subscription_status: 'none',
      phone: client.phone ?? null,
      lifecycle: client.lifecycle ?? 'active',
      start_date: client.startDate ?? null,
      goals: client.goals ?? null,
      injuries: client.injuries ?? null,
    });
    reportWrite('client', error);
  },

  // Bulk import behind the 3-step wizard. Deliberately NOT a loop over addClient:
  // chunked array inserts (2 round trips for 200 rows, not 200), exact ok/failed
  // counts back to the wizard, local state committed only per confirmed chunk,
  // and no per-row toast spam — the wizard owns all messaging. Notes are written
  // to coaching_notes (their own table; a profile column for them doesn't exist).
  importClients: async (rows) => {
    const coach = get().user;
    if (!coach) return { ok: 0, failed: rows.length, ids: [] };

    const prepared = rows.map(({ draft, note }) => {
      const id = newId();
      const noteRow = note?.trim()
        ? { id: newId(), client_id: id, coach_id: coach.id, text: note.trim(), created_at: new Date().toISOString() }
        : null;
      const local: Client = {
        id,
        name: draft.name || 'Client',
        email: draft.email || '',
        role: 'client',
        coachId: coach.id,
        status: 'green',
        engagementScore: 100,
        subscriptionStatus: 'none',
        phone: draft.phone,
        lifecycle: draft.lifecycle ?? 'active',
        startDate: draft.startDate,
        goals: draft.goals,
        injuries: draft.injuries,
        coachingNotes: noteRow ? [{ id: noteRow.id, text: noteRow.text, createdAt: noteRow.created_at }] : [],
      };
      const insert = {
        id,
        name: local.name,
        email: local.email || null,
        role: 'client',
        coach_id: coach.id,
        status: 'green',
        engagement_score: 100,
        subscription_status: 'none',
        phone: local.phone ?? null,
        lifecycle: local.lifecycle ?? 'active',
        start_date: local.startDate ?? null,
        goals: local.goals ?? null,
        injuries: local.injuries ?? null,
      };
      return { local, insert, noteRow };
    });

    if (!isLive()) {
      set((state) => ({ clients: [...state.clients, ...prepared.map((p) => p.local)] }));
      return { ok: prepared.length, failed: 0, ids: prepared.map((p) => p.local.id) };
    }

    const CHUNK = 100;
    let ok = 0;
    let failed = 0;
    const ids: string[] = [];
    for (let i = 0; i < prepared.length; i += CHUNK) {
      const chunk = prepared.slice(i, i + CHUNK);
      const { error } = await supabase.from('profiles').insert(chunk.map((p) => p.insert));
      if (error) {
        console.error('importClients chunk failed:', error.message);
        failed += chunk.length;
        continue;
      }
      ok += chunk.length;
      ids.push(...chunk.map((p) => p.local.id));
      set((state) => ({ clients: [...state.clients, ...chunk.map((p) => p.local)] }));
      const notes = chunk.map((p) => p.noteRow).filter((n): n is NonNullable<typeof n> => n != null);
      if (notes.length > 0) {
        const { error: noteErr } = await supabase.from('coaching_notes').insert(notes);
        if (noteErr) console.error('importClients notes failed:', noteErr.message);
      }
    }
    return { ok, failed, ids };
  },

  /* Full training-history import — the user-facing version of the original
     hand-built seed. One client; sessions land status:'completed' so they read
     as history, not plans. Never overwrites: a date that already has a session
     (or a week that already has a check-in) is skipped, counted, reported. */
  importHistory: async (clientId, plan) => {
    const coach = get().user;
    if (!coach) return { sessions: 0, checkIns: 0, skipped: 0, failed: 0 };

    const existingDates = new Set(get().sessions.filter((s) => s.clientId === clientId).map((s) => s.date));
    const existingWeeks = new Set(get().checkIns.filter((c) => c.clientId === clientId).map((c) => c.weekOf));

    const sessions: Session[] = plan.sessions
      .filter((s) => !existingDates.has(s.date))
      .map((s) => ({
        id: newId(), clientId, coachId: coach.id,
        date: s.date, title: s.title, sections: s.sections, status: 'completed' as const,
      }));
    // The schema allows ONE check-in per client per week — when two sheet
    // columns land in the same week, the later measurement wins.
    const byWeek = new Map<string, (typeof plan.checkIns)[number]>();
    for (const c of plan.checkIns) {
      if (existingWeeks.has(c.weekOf)) continue;
      const prev = byWeek.get(c.weekOf);
      if (!prev || c.date >= prev.date) byWeek.set(c.weekOf, c);
    }
    const checkIns: CheckIn[] = [...byWeek.values()]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((c) => ({
        id: newId(), clientId, coachId: coach.id,
        weekOf: c.weekOf, weightLbs: c.weightLbs, bodyFatPct: c.bodyFatPct,
        skeletalMuscleMass: c.skeletalMuscleMass, sleep: c.sleep, diet: c.diet,
        recovery: c.recovery, notes: c.notes, createdAt: `${c.date}T12:00:00.000Z`,
      }));
    const skipped = (plan.sessions.length - sessions.length) + (plan.checkIns.length - checkIns.length);

    if (!isLive()) {
      set((state) => ({ sessions: [...state.sessions, ...sessions], checkIns: [...state.checkIns, ...checkIns] }));
      if (plan.dietNote) get().addCoachingNote(clientId, plan.dietNote);
      return { sessions: sessions.length, checkIns: checkIns.length, skipped, failed: 0 };
    }

    const CHUNK = 100;
    let okSessions = 0;
    let okCheckIns = 0;
    let failed = 0;
    for (let i = 0; i < sessions.length; i += CHUNK) {
      const chunk = sessions.slice(i, i + CHUNK);
      const { error } = await supabase.from('sessions').insert(chunk.map((s) => toRow(s)));
      if (error) {
        console.error('importHistory sessions chunk failed:', error.message);
        failed += chunk.length;
        continue;
      }
      okSessions += chunk.length;
      set((state) => ({ sessions: [...state.sessions, ...chunk] }));
    }
    for (let i = 0; i < checkIns.length; i += CHUNK) {
      const chunk = checkIns.slice(i, i + CHUNK);
      const { error } = await supabase.from('check_ins').insert(chunk.map((c) => toRow(c)));
      if (error) {
        console.error('importHistory check-ins chunk failed:', error.message);
        failed += chunk.length;
        continue;
      }
      okCheckIns += chunk.length;
      set((state) => ({ checkIns: [...state.checkIns, ...chunk] }));
    }
    if (plan.dietNote) get().addCoachingNote(clientId, plan.dietNote);
    return { sessions: okSessions, checkIns: okCheckIns, skipped, failed };
  },

  updateClientStatus: async (clientId: string, status: ClientStatus) => {
    set((state) => ({ clients: state.clients.map((c) => (c.id === clientId ? { ...c, status } : c)) }));
    if (!isLive()) return;
    const { error } = await supabase.from('profiles').update({ status }).eq('id', clientId);
    reportWrite('client status', error);
  },

  // Invite a managed client to the app. The edge function sends a one-time email
  // invitation and links the resulting auth login to this managed profile.
  inviteClient: async (clientId: string, email: string) => {
    const { data, error } = await supabase.functions.invoke('invite-client', {
      body: {
        clientProfileId: clientId,
        email,
        redirectTo: `${window.location.origin}/set-password`,
      },
    });
    if (error) {
      let msg = error.message;
      try { msg = (await (error as { context?: { json: () => Promise<{ error?: string }> } }).context!.json()).error ?? msg; } catch { /* keep error.message */ }
      console.error('[fitflex] invite failed:', msg);
      get().showToast(msg, 'error');
      return null;
    }
    const result = data as { userId: string; email: string };
    set((state) => ({ clients: state.clients.map((c) => (c.id === clientId ? { ...c, userId: result.userId, email: result.email } : c)) }));
    return result;
  },

  addMasterProgram: async (program: Program) => {
    const user = get().user;
    if (!user) return;
    const id = isUuid(program.id) ? program.id : newId();
    const local: Program = { ...program, id, coachId: user.id };
    set((state) => ({ masterPrograms: [...state.masterPrograms.filter((p) => p.id !== id), local] }));
    if (!isLive()) return;
    const { error } = await supabase.from('master_programs').insert({
      id,
      title: program.title,
      coach_id: user.id,
      workouts: program.workouts,
      is_locked: program.isLocked ?? false,
    });
    reportWrite('program', error);
  },

  deleteMasterProgram: async (programId: string) => {
    set((state) => ({ masterPrograms: state.masterPrograms.filter((p) => p.id !== programId) }));
    if (!isLive()) return;
    const { error } = await supabase.from('master_programs').delete().eq('id', programId);
    reportWrite('program delete', error);
  },

  upsertGlobalExercise: (exercise: GlobalExercise) => {
    set((state) => ({
      globalExercises: state.globalExercises.some((e) => e.id === exercise.id)
        ? state.globalExercises.map((e) => (e.id === exercise.id ? exercise : e))
        : [...state.globalExercises, exercise],
    }));
    if (isLive()) {
      supabase.from('global_exercises').upsert(toRow(exercise)).then(({ error }) => reportWrite('exercise', error));
    }
  },

  updateMasterProgram: async (program: Program) => {
    set((state) => ({ masterPrograms: state.masterPrograms.map((p) => (p.id === program.id ? program : p)) }));
    if (!isLive()) return;
    const { error } = await supabase.from('master_programs')
      .update({ title: program.title, workouts: program.workouts, is_locked: program.isLocked ?? false })
      .eq('id', program.id);
    reportWrite('program', error);
  },

  updateHabit: async (clientId: string, date: string, habitName: string, completed: boolean) => {
    // Merge into the client's habits jsonb (Postgres has no dot-path update, so we
    // rewrite the whole map from local state). RLS lets a client update own profile.
    const client = get().clients.find((c) => c.id === clientId);
    const habits = { ...(client?.habits || {}) };
    habits[date] = { ...(habits[date] || {}), [habitName]: completed };
    set((state) => ({ clients: state.clients.map((c) => (c.id === clientId ? { ...c, habits } : c)) }));
    if (!isLive()) return;
    const { error } = await supabase.from('profiles').update({ habits }).eq('id', clientId);
    reportWrite('habit', error);
  },

  addResource: async (resourceData: Omit<Resource, 'id' | 'coachId' | 'createdAt'>) => {
    const user = get().user;
    if (!user) return;
    const resource: Resource = { ...resourceData, id: newId(), coachId: user.id, createdAt: new Date().toISOString() };
    set((state) => ({ resources: [resource, ...state.resources] }));
    if (!isLive()) return;
    const { error } = await supabase.from('resources').insert({
      id: resource.id,
      coach_id: user.id,
      title: resource.title,
      description: resource.description ?? null,
      url: resource.url,
      type: resource.type,
      created_at: resource.createdAt,
    });
    reportWrite('resource', error);
  },

  deleteResource: async (id: string) => {
    set((state) => ({ resources: state.resources.filter((r) => r.id !== id) }));
    if (!isLive()) return;
    const { error } = await supabase.from('resources').delete().eq('id', id);
    reportWrite('resource delete', error);
  },

  uploadProgressPhoto: async (file: Blob, category: ProgressPhoto['category'], weight?: string) => {
    const user = get().user;
    if (!user) return;
    const today = new Date().toISOString().split('T')[0];
    const photo: ProgressPhoto = { id: newId(), clientId: user.id, url: '', date: today, category, weight: weight || '', createdAt: new Date().toISOString() };
    if (!isLive()) {
      photo.url = URL.createObjectURL(file);
      set((state) => ({ progressPhotos: [photo, ...state.progressPhotos] }));
      return;
    }
    // Private client-photos bucket — {coachId}/{clientId}/{uuid}.jpg; storage
    // policies scope coaches by folder 1 and clients by folder 2. The table
    // stores the PATH; loaders sign it for display.
    const path = `${user.coachId ?? user.id}/${user.id}/${photo.id}.jpg`;
    const { error: upErr } = await supabase.storage.from('client-photos').upload(path, file, { contentType: file.type || 'image/jpeg' });
    if (upErr) { reportWrite('photo upload', upErr); return; }
    const { error } = await supabase.from('progress_photos').insert({
      id: photo.id, client_id: user.id, url: path, date: today, category, weight: weight || '',
    });
    reportWrite('photo', error);
    const { data: signed } = await supabase.storage.from('client-photos').createSignedUrl(path, 3600);
    photo.url = signed?.signedUrl || '';
    set((state) => ({ progressPhotos: [photo, ...state.progressPhotos] }));
  },

  checkAchievements: async () => {
    const { user, workoutLogs, achievements } = get();
    if (!user) return [];
    const newAchievements: Achievement[] = [];
    const unlockedIds = new Set(achievements.map((a) => a.id));
    if (workoutLogs.length === 1 && !unlockedIds.has('milestone_first_workout')) {
      newAchievements.push({ id: 'milestone_first_workout', title: 'First Step Taken', description: 'You completed your first ever workout!', icon: '🚀', unlockedAt: new Date().toISOString(), type: 'MILESTONE' });
    }
    if (newAchievements.length === 0) return [];
    set((state) => ({ achievements: [...state.achievements, ...newAchievements] }));
    // (The activity-feed celebration post returns with the feed in Phase 6.)
    if (isLive()) {
      for (const ach of newAchievements) {
        const { error } = await supabase.from('achievements').insert({
          id: newId(), client_id: user.id, title: ach.title, description: ach.description, icon: ach.icon, type: ach.type, unlocked_at: ach.unlockedAt,
        });
        reportWrite('achievement', error);
      }
    }
    return newAchievements;
  },

  logMeal: async (mealData: Omit<Meal, 'id' | 'loggedAt'>) => {
    const user = get().user;
    if (!user) return;
    const today = new Date().toISOString().split('T')[0];
    const newMeal: Meal = { ...mealData, id: `meal_${Date.now()}`, loggedAt: new Date().toISOString() };
    const existing = get().nutritionLogs.find((l) => l.clientId === user.id && l.date === today);
    const log: NutritionLog = existing
      ? { ...existing, meals: [...existing.meals, newMeal] }
      : { id: newId(), clientId: user.id, date: today, meals: [newMeal] };
    set((state) => ({
      nutritionLogs: existing
        ? state.nutritionLogs.map((l) => (l.id === log.id ? log : l))
        : [...state.nutritionLogs, log],
    }));
    if (!isLive()) return;
    // One row per client per day (unique index) — upsert keeps it that way.
    const { error } = await supabase.from('nutrition_logs').upsert(
      { id: log.id, client_id: user.id, date: today, meals: log.meals },
      { onConflict: 'client_id,date' }
    );
    reportWrite('meal', error);
  },

  updateMacroGoals: async (clientId: string, goals: User['macroGoals']) => {
    set((state) => ({
      user: state.user && state.user.id === clientId ? { ...state.user, macroGoals: goals } : state.user,
      clients: state.clients.map((c) => (c.id === clientId ? { ...c, macroGoals: goals } : c)),
    }));
    if (!isLive()) return;
    const { error } = await supabase.from('profiles').update({ macro_goals: goals ?? null }).eq('id', clientId);
    reportWrite('macros', error);
  },

  /* ---------- Weekly Programming (sessions on the board) ----------
     Local state is the source of truth for the UI; Firestore writes are
     best-effort and skipped entirely in demo mode. */
  upsertSession: (session: Session) => {
    set((state) => {
      const exists = state.sessions.some((s) => s.id === session.id);
      return {
        sessions: exists
          ? state.sessions.map((s) => (s.id === session.id ? session : s))
          : [...state.sessions, session],
      };
    });
    if (isLive()) {
      supabase.from('sessions').upsert(toRow(session)).then(({ error }) => reportWrite('session', error));
    }
  },

  deleteSession: (sessionId: string) => {
    set((state) => ({ sessions: state.sessions.filter((s) => s.id !== sessionId) }));
    if (isLive()) {
      supabase.from('sessions').delete().eq('id', sessionId).then(({ error }) => reportWrite('session delete', error));
    }
  },

  // A client advancing their OWN session (planned→partial→completed) through the
  // SECURITY DEFINER RPC — clients have no direct sessions UPDATE policy, and the
  // RPC is status-only so they can't rewrite the prescribed workout.
  setSessionStatus: (sessionId, status) => {
    set((state) => ({ sessions: state.sessions.map((s) => (s.id === sessionId ? { ...s, status } : s)) }));
    if (!isLive()) return;
    supabase.rpc('set_session_status', { p_session_id: sessionId, p_status: status }).then(({ error }) => reportWrite('progress', error));
  },

  saveWeekAsProgram: (clientId: string, weekStartIso: string, programTitle: string) => {
    const user = get().user;
    if (!user) return;
    // Week = 7 days starting at weekStartIso (a Monday)
    const end = new Date(weekStartIso + 'T00:00:00');
    end.setDate(end.getDate() + 6);
    const weekEndIso = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
    const weekSessions = get().sessions
      .filter((s) => s.clientId === clientId && s.date >= weekStartIso && s.date <= weekEndIso)
      .sort((a, b) => a.date.localeCompare(b.date));
    if (weekSessions.length === 0) return;

    // Flatten sessions into the legacy Program shape so the Library/ProgramEditor can use it
    const program: Program = {
      id: newId(),
      title: programTitle,
      coachId: user.id,
      orgId: user.orgId,
      workouts: weekSessions.map((sess) => ({
        id: `w_${sess.id}`,
        title: sess.title,
        exercises: sess.sections.flatMap((sec) =>
          sec.exercises.map((pe) => ({
            id: pe.id,
            name: pe.name,
            sets: Math.max(pe.sets.length, 1),
            reps:
              pe.sets[0]?.reps ??
              (pe.sets[0]?.seconds ? `${pe.sets[0].seconds}s` : pe.sets[0]?.laps ? `${pe.sets[0].laps} laps` : ''),
            weight: pe.sets[0]?.load,
            supersetId: pe.supersetId,
          }))
        ),
      })),
    };

    set((state) => ({
      masterPrograms: [...state.masterPrograms.filter((p) => p.id !== program.id), program],
    }));
    if (isLive()) {
      supabase.from('master_programs')
        .insert({ id: program.id, title: program.title, coach_id: user.id, workouts: program.workouts, is_locked: false })
        .then(({ error }) => reportWrite('program', error));
    }
  },

  /* ---------- CRM (per-client hub) ----------
     Local state is the UI source of truth; Firestore writes are best-effort
     and skipped in demo mode (same pattern as the session actions). */
  updateClientCrm: (clientId, patch) => {
    set((state) => ({
      clients: state.clients.map((c) => (c.id === clientId ? { ...c, ...patch } : c)),
    }));
    if (isLive()) {
      // toRow drops undefined values, but "clear the plan/rate" must reach the
      // DB as an explicit null — map those keys by hand when present-but-unset.
      const row = toRow(patch) as Record<string, unknown>;
      const NULLABLE: Record<string, string> = { planId: 'plan_id', priceCents: 'price_cents', billingInterval: 'billing_interval' };
      (Object.keys(NULLABLE) as (keyof typeof NULLABLE)[]).forEach((k) => {
        if (k in patch && (patch as Record<string, unknown>)[k] === undefined) row[NULLABLE[k]] = null;
      });
      supabase.from('profiles').update(row).eq('id', clientId).then(({ error }) => reportWrite('client', error));
    }
  },

  addCoachingNote: (clientId, text) => {
    const body = text.trim();
    if (!body) return;
    const coachId = get().user?.id;
    const note: CoachingNote = { id: newId(), text: body, createdAt: new Date().toISOString() };
    set((state) => ({
      clients: state.clients.map((c) =>
        c.id === clientId ? { ...c, coachingNotes: [note, ...(c.coachingNotes || [])] } : c
      ),
    }));
    if (isLive()) {
      supabase.from('coaching_notes')
        .insert({ id: note.id, client_id: clientId, coach_id: coachId, text: body, created_at: note.createdAt })
        .then(({ error }) => reportWrite('note', error));
    }
  },

  deleteCoachingNote: (clientId, noteId) => {
    set((state) => ({
      clients: state.clients.map((c) =>
        c.id === clientId ? { ...c, coachingNotes: (c.coachingNotes || []).filter((n) => n.id !== noteId) } : c
      ),
    }));
    if (isLive()) {
      supabase.from('coaching_notes').delete().eq('id', noteId).then(({ error }) => reportWrite('note delete', error));
    }
  },

  /* ---------- Weekly Check-ins ---------- */
  upsertCheckIn: (checkIn: CheckIn) => {
    set((state) => {
      const exists = state.checkIns.some((c) => c.id === checkIn.id);
      return {
        checkIns: exists
          ? state.checkIns.map((c) => (c.id === checkIn.id ? checkIn : c))
          : [...state.checkIns, checkIn],
      };
    });
    if (isLive()) {
      supabase.from('check_ins').upsert(toRow(checkIn)).then(({ error }) => reportWrite('check-in', error));
    }
  },

  deleteCheckIn: (checkInId: string) => {
    set((state) => ({ checkIns: state.checkIns.filter((c) => c.id !== checkInId) }));
    if (isLive()) {
      supabase.from('check_ins').delete().eq('id', checkInId).then(({ error }) => reportWrite('check-in delete', error));
    }
  },

  /* ---------- Coaching pricing plans (money, not training content) ---------- */
  upsertCoachingPlan: (plan: CoachingPlan) => {
    set((state) => {
      const exists = state.coachingPlans.some((p) => p.id === plan.id);
      return {
        coachingPlans: exists
          ? state.coachingPlans.map((p) => (p.id === plan.id ? plan : p))
          : [plan, ...state.coachingPlans],
      };
    });
    if (isLive()) {
      supabase.from('coaching_plans').upsert(toRow(plan)).then(({ error }) => reportWrite('plan', error));
    }
  },

  deleteCoachingPlan: (planId: string) => {
    // Mirror the FK's ON DELETE SET NULL locally: enrolled clients keep their
    // copied rate; only the plan link clears.
    set((state) => ({
      coachingPlans: state.coachingPlans.filter((p) => p.id !== planId),
      clients: state.clients.map((c) => (c.planId === planId ? { ...c, planId: undefined } : c)),
    }));
    if (isLive()) {
      supabase.from('coaching_plans').delete().eq('id', planId).then(({ error }) => reportWrite('plan delete', error));
    }
  },

  // Refetch the webhook-synced billing cache (RLS scopes rows to the caller —
  // the owning coach sees the roster's rows, a client sees only their own).
  reloadBilling: async () => {
    if (!isLive()) return;
    const { data, error } = await supabase.from('client_billing').select('*');
    if (!error && data) set({ clientBilling: data.map((r) => fromRow<ClientBilling>(r)) });
  },

  // Re-read the signed-in user's own profile row — Stripe Connect status and
  // account id are written server-side (edge function + webhook), so the local
  // user object goes stale after onboarding completes in another tab.
  refreshSelf: async () => {
    const user = get().user;
    if (!user || !isLive()) return;
    const { data } = await supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
    if (data) set({ user: fromRow<User>(data) });
  },

  /* ---------- Body map triage (coach feedback on a client's marked zone) ---------- */
  upsertBodyMapTriage: (triage: BodyMapTriage) => {
    set((state) => {
      const exists = state.bodyMapTriage.some(
        (t) => t.clientId === triage.clientId && t.zoneId === triage.zoneId && t.category === triage.category
      );
      return {
        bodyMapTriage: exists
          ? state.bodyMapTriage.map((t) =>
              t.clientId === triage.clientId && t.zoneId === triage.zoneId && t.category === triage.category ? triage : t
            )
          : [...state.bodyMapTriage, triage],
      };
    });
    if (isLive()) {
      supabase
        .from('body_map_triage')
        .upsert(toRow(triage), { onConflict: 'client_id,zone_id,category' })
        .then(({ error }) => reportWrite('body-map triage', error));
    }
  },

  /* ---------- Leads ---------- */
  upsertLead: (lead: Lead) => {
    set((state) => {
      const exists = state.leads.some((l) => l.id === lead.id);
      return {
        leads: exists ? state.leads.map((l) => (l.id === lead.id ? lead : l)) : [...state.leads, lead],
      };
    });
    if (isLive()) {
      supabase.from('leads').upsert(toRow(lead)).then(({ error }) => reportWrite('lead', error));
    }
  },

  deleteLead: (leadId: string) => {
    set((state) => ({ leads: state.leads.filter((l) => l.id !== leadId) }));
    if (isLive()) {
      supabase.from('leads').delete().eq('id', leadId).then(({ error }) => reportWrite('lead delete', error));
    }
  },

  convertLeadToClient: (leadId: string) => {
    const lead = get().leads.find((l) => l.id === leadId);
    const coach = get().user;
    if (!lead || !coach) return;
    const clientId = newId();
    const note: CoachingNote | null = lead.notes
      ? { id: newId(), text: `From lead: ${lead.notes}`, createdAt: new Date().toISOString() }
      : null;
    const client: Client = {
      id: clientId,
      name: lead.name,
      email: lead.email || '',
      role: 'client',
      coachId: coach.id,
      status: 'green',
      engagementScore: 100,
      subscriptionStatus: 'none',
      phone: lead.phone,
      lifecycle: 'active',
      startDate: lead.consultAt,
      coachingNotes: note ? [note] : [],
    };
    // Add to the roster and mark the lead won (kept for the funnel/history).
    set((state) => ({
      clients: [...state.clients, client],
      leads: state.leads.map((l) => (l.id === leadId ? { ...l, status: 'won' as const } : l)),
    }));
    if (!isLive()) return;
    // Persist as: new managed-client profile → its first coaching note → lead won.
    (async () => {
      const { error: cErr } = await supabase.from('profiles').insert({
        id: clientId,
        name: client.name,
        email: client.email || null,
        role: 'client',
        coach_id: coach.id,
        status: 'green',
        engagement_score: 100,
        subscription_status: 'none',
        phone: lead.phone ?? null,
        lifecycle: 'active',
        start_date: lead.consultAt ?? null,
      });
      reportWrite('client', cErr);
      if (note && !cErr) {
        const { error: nErr } = await supabase.from('coaching_notes')
          .insert({ id: note.id, client_id: clientId, coach_id: coach.id, text: note.text, created_at: note.createdAt });
        reportWrite('note', nErr);
      }
      const { error: lErr } = await supabase.from('leads').update({ status: 'won' }).eq('id', leadId);
      reportWrite('lead', lErr);
    })();
  },

  upsertAppointment: (appointment: Appointment) => {
    set((state) => {
      const exists = state.appointments.some((a) => a.id === appointment.id);
      return {
        appointments: exists
          ? state.appointments.map((a) => (a.id === appointment.id ? appointment : a))
          : [...state.appointments, appointment],
      };
    });
    if (isLive()) {
      // toRow converts top-level keys only; rrule/exdate pass through verbatim.
      supabase.from('appointments').upsert(toRow(appointment)).then(({ error }) => reportWrite('appointment', error));
    }
  },

  deleteAppointment: (appointmentId: string) => {
    set((state) => ({ appointments: state.appointments.filter((a) => a.id !== appointmentId) }));
    if (isLive()) {
      supabase.from('appointments').delete().eq('id', appointmentId).then(({ error }) => reportWrite('appointment delete', error));
    }
  },

  // After the calendar-sync edge function reconciles server-side, pull the result.
  reloadAppointments: async () => {
    const user = get().user;
    if (!user || !isLive()) return;
    const { data, error } = await supabase.from('appointments').select('*').eq('coach_id', user.id);
    if (!error && data) set({ appointments: data.map((r) => fromRow<Appointment>(r)) });
  },

  // Cancel a single occurrence of a recurring series by adding its start to the
  // EXDATE list (a whole-row update through the normal upsert path). The agenda
  // and the .ics feed both drop occurrences whose start is in exdate.
  cancelOccurrence: (appointmentId: string, occurrenceStartIso: string) => {
    const appt = get().appointments.find((a) => a.id === appointmentId);
    if (!appt || appt.exdate.includes(occurrenceStartIso)) return;
    get().upsertAppointment({ ...appt, exdate: [...appt.exdate, occurrenceStartIso] });
  },

  // Roll the coach's .ics feed token — invalidates any existing subscriptions.
  regenerateFeedToken: async () => {
    const user = get().user;
    if (!user) return;
    const token = newId();
    set({ user: { ...user, calendarFeedToken: token } });
    if (!isLive()) return;
    const { error } = await supabase.from('profiles').update({ calendar_feed_token: token }).eq('id', user.id);
    reportWrite('calendar feed', error);
  },

  // Log one set against a session (workoutId = session.id; workout_id is a text
  // column so the uuid fits). One workout_logs row per client-session-day: find
  // the in-progress one in local state and append, else start a fresh uuid row.
  // (Session status feedback is deferred — clients have no sessions UPDATE policy.)
  logSet: async (workoutId: string, workoutTitle: string, exerciseId: string, weight: string, reps: string, videoUrl?: string) => {
    const user = get().user;
    if (!user) return;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const newSet: LoggedSet = { weight, reps, completedAt: now.toISOString(), formVideoUrl: videoUrl || undefined };

    const existing = get().workoutLogs.find((l) => l.clientId === user.id && l.workoutId === workoutId && l.date === today);
    const log: WorkoutLog = existing
      ? { ...existing, exercises: { ...existing.exercises, [exerciseId]: [...(existing.exercises[exerciseId] || []), newSet] } }
      : { id: newId(), clientId: user.id, workoutId, workoutTitle, date: today, exercises: { [exerciseId]: [newSet] } };

    set((state) => ({
      workoutLogs: existing
        ? state.workoutLogs.map((l) => (l.id === log.id ? log : l))
        : [...state.workoutLogs, log],
    }));

    // First set of this session → mark it in progress (completion flips it to done).
    if (!existing) {
      const sess = get().sessions.find((s) => s.id === workoutId);
      if (sess?.status === 'planned') get().setSessionStatus(workoutId, 'partial');
    }

    if (!isLive()) return;
    const { error } = await supabase.from('workout_logs').upsert(toRow(log));
    reportWrite('set', error);
  },

  addFeedback: async (logId: string, exerciseId: string, setIndex: number, feedback: string) => {
    // Update local state first — the source of truth for the UI, and the only store
    // in demo mode (where the log lives locally and getDoc would return nothing).
    set((state) => ({
      workoutLogs: state.workoutLogs.map((log) => {
        if (log.id !== logId) return log;
        const sets = [...(log.exercises[exerciseId] || [])];
        if (!sets[setIndex]) return log;
        sets[setIndex] = { ...sets[setIndex], coachFeedback: feedback };
        return { ...log, exercises: { ...log.exercises, [exerciseId]: sets } };
      }),
    }));
    if (!isLive()) return;
    // Persist the whole exercises jsonb for this log. A coach updating a client's
    // log is allowed by RLS (workout_logs_coach_update = is_my_client).
    const log = get().workoutLogs.find((l) => l.id === logId);
    if (log) {
      const { error } = await supabase.from('workout_logs').update({ exercises: log.exercises }).eq('id', logId);
      reportWrite('feedback', error);
    }
  },

  sendMessage: async (receiverId: string, text: string) => {
    const user = get().user;
    if (!user) return;
    const msg: Message = { id: newId(), senderId: user.id, receiverId, text, createdAt: new Date().toISOString(), read: false };
    set((state) => ({ messages: [...state.messages, msg] }));
    if (!isLive()) return;
    const { error } = await supabase.from('messages').insert(toRow(msg));
    reportWrite('message', error);
  },

  markMessagesRead: async (senderId: string) => {
    const user = get().user;
    if (!user) return;
    set((state) => ({
      messages: state.messages.map((m) => (m.senderId === senderId && m.receiverId === user.id ? { ...m, read: true } : m)),
    }));
    if (!isLive()) return;
    const { error } = await supabase.from('messages').update({ read: true })
      .eq('sender_id', senderId)
      .eq('receiver_id', user.id)
      .eq('read', false);
    reportWrite('messages read', error);
  },

  // Load the signed-in user's data slice from Supabase, by role. RLS scopes every
  // table to the caller. (Realtime is a later layer; for one user on one device,
  // optimistic writes + reload-on-refresh already cover it.)
  initDataSync: (user: User) => {
    set({ user, isLoading: false });
    if (!isLive()) return; // demo data is already seeded in local state
    if (user.role === 'coach' || user.role === 'owner') {
      loadCoachData(user.id);
    } else {
      loadClientData(user.id);
    }
  },

  // First-run tour sandbox: overlay the demo dataset on the REAL signed-in
  // coach, in memory only. isLive() goes false while tourMode is set, so every
  // interaction during the tour mutates the sandbox and nothing reaches the DB.
  // In-memory by design — a mid-tour refresh reloads clean real data.
  startTour: () => {
    const user = get().user;
    if (!user) return;
    const demo = buildDemoData(user.id);
    set({
      ...demo,
      // keep the coach's real exercise library when they have one
      globalExercises: get().globalExercises.length ? get().globalExercises : demo.globalExercises,
      tourMode: true,
    });
  },

  endTour: () => {
    const user = get().user;
    // Clear every slice the sandbox touched (loadCoachData does not reset
    // messages/feedEvents/locations), then refetch the real data.
    set({
      tourMode: false,
      clients: [], sessions: [], checkIns: [], leads: [], appointments: [],
      coachingPlans: [], clientBilling: [],
      masterPrograms: [], workoutLogs: [], messages: [], feedEvents: [],
      locations: [], currentLocationId: null, dataLoaded: false,
    });
    if (user) get().initDataSync(user);
  },

  enterDemoMode: () => {
    localStorage.setItem('fitflex_demo', 'true');
    const demoUser: User = {
      id: 'demo-coach-001',
      name: 'Alex Rivera',
      role: 'coach',
      email: 'alex@demo.fitflex',
      isIncognito: false,
    };

    const demo = buildDemoData(demoUser.id);
    set({
      user: demoUser,
      isLoading: false,
      dataLoaded: true,
      ...demo,
    });
  },
  };
});
